import type { StudioTestCase } from "@/components/ai/chat/AiChatSidebar";
import type { PipelineView } from "@/pages/NewStudio/pipelineView";
import type {
  AssertType,
  MiddlewareConfig,
  StageConfig,
  TestSuiteCase,
} from "@/types/api";
import type { LintStatus } from "./pipelineLint";
import type { ScriptRunOutput, TestRunView } from "./scriptRun";
import type { StagedResource, TestAssertionDraft } from "./stagingModel";

// What the AI Helper is told about the new Studio, derived from the
// staging state — pure so the pieces are testable. The sidebar's prop
// contract (AiChatSidebarProps) is the old Studio's; these helpers map
// the new Studio onto it.

function isStage(value: unknown): value is StageConfig {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as StageConfig).service_name === "string" &&
    typeof (value as StageConfig).resource === "string"
  );
}

function stagesOf(value: unknown): MiddlewareConfig[] {
  return Array.isArray(value) ? value.filter(isStage) : [];
}

const EMPTY_VIEW: Omit<PipelineView, "parseError"> = {
  name: "",
  enabled: true,
  source: null,
  middlewares: [],
  sinks: [],
  schemas: [],
  checkpoint: null,
  batch: null,
};

/** The pipeline code as the sidebar's PipelineView: lenient, so a draft
 *  missing whole sections still describes the stages it has; a document
 *  that isn't JSON carries the parse error (the item is then skipped). */
export function pipelineViewFromCode(code: string): PipelineView {
  let doc: unknown;
  try {
    doc = JSON.parse(code);
  } catch (error) {
    const message = error instanceof Error ? error.message : "invalid JSON";
    return { ...EMPTY_VIEW, parseError: message, code };
  }
  if (typeof doc !== "object" || doc === null) {
    return { ...EMPTY_VIEW, parseError: "not a pipeline document", code };
  }
  const d = doc as Record<string, unknown>;
  return {
    name: typeof d.name === "string" ? d.name : "",
    enabled: d.enabled !== false,
    source: isStage(d.source) ? d.source : null,
    middlewares: stagesOf(d.middlewares),
    sinks: stagesOf(d.sinks) as StageConfig[],
    schemas: [],
    checkpoint: (d.checkpoint as PipelineView["checkpoint"]) ?? null,
    batch: (d.batch as PipelineView["batch"]) ?? null,
    parseError: null,
  };
}

/** Stage services the registry doesn't know — the graph's dashed cards. */
export function unresolvedServicesIn(
  view: PipelineView,
  known: Set<string> | null,
): Set<string> | undefined {
  if (!known) return undefined;
  const stages = [
    ...(view.source ? [view.source] : []),
    ...view.middlewares,
    ...view.sinks,
  ];
  const unresolved = new Set(
    stages.map((s) => s.service_name).filter((n) => !known.has(n)),
  );
  return unresolved.size > 0 ? unresolved : undefined;
}

/** The one error the helper should hear about, most recent surface
 *  first: a failed lifecycle action, a failed run, then lint errors on
 *  the pipeline code. */
export function studioLastError(input: {
  actionError: string | null;
  run: ScriptRunOutput | null;
  lintStatus: LintStatus;
  codeIssues: string[];
}): string | undefined {
  if (input.actionError) return input.actionError;
  if (input.run && !input.run.success) {
    return `${input.run.scriptName}: ${input.run.text}`;
  }
  if (input.lintStatus === "errors" && input.codeIssues.length > 0) {
    return `Pipeline code has errors:\n${input.codeIssues
      .map((issue) => `- ${issue}`)
      .join("\n")}`;
  }
  return undefined;
}

function assertionValue(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

function caseResult(
  name: string,
  run: TestRunView | undefined,
): Pick<StudioTestCase, "passed" | "error"> {
  const result = run?.cases.find((c) => c.name === name);
  if (!result) return {};
  return {
    passed: result.passed,
    ...(result.error ? { error: result.error } : {}),
  };
}

/** The staged tests as the sidebar's cases: each row's assertion lines,
 *  with the last run's verdict when the run covered that case. */
export function testCasesFor(
  tests: StagedResource[],
  assertions: Record<string, TestAssertionDraft[]>,
  run: TestRunView | undefined,
): StudioTestCase[] {
  return tests.map((test) => ({
    name: test.name,
    input: null,
    assertions: (assertions[test.id] ?? [])
      .filter((row) => row.path.trim() !== "")
      .map((row) => ({
        path: row.path,
        assert_type: row.op,
        expected_value: assertionValue(row.value),
      })),
    ...caseResult(test.name, run),
  }));
}

function isGeneratedCase(value: unknown): value is {
  name: string;
  expected?: unknown;
  assertions?: unknown[];
} {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { name?: unknown }).name === "string"
  );
}

function toAssertion(value: unknown) {
  const a = value as {
    path?: unknown;
    assert_type?: unknown;
    expected_value?: unknown;
  };
  if (typeof a?.path !== "string" || typeof a?.assert_type !== "string") {
    return null;
  }
  return {
    path: a.path,
    assert_type: a.assert_type as AssertType,
    expected_value: a.expected_value,
  };
}

/** A `json:tests:<name>` fence → suite cases (the save_test_suite shape:
 *  `[{name, expected, assertions: [{path, assert_type, expected_value}]}]`).
 *  Null when the fence isn't that shape. */
export function generatedCasesFromJson(code: string): TestSuiteCase[] | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(code);
  } catch {
    return null;
  }
  const list = Array.isArray(parsed) ? parsed : [parsed];
  if (!list.every(isGeneratedCase)) return null;
  return list.map((c) => ({
    name: c.name,
    expected: c.expected,
    assertions: (c.assertions ?? [])
      .map(toAssertion)
      .filter((a): a is NonNullable<typeof a> => a !== null),
    strict_mode: false,
  }));
}

/** The remembered saved scripts with one more read — a new map, so a
 *  memo keyed on the cache recomputes once the fetch lands. */
export function withSavedScript(
  saved: ReadonlyMap<string, string>,
  id: string,
  content: string,
): ReadonlyMap<string, string> {
  return new Map(saved).set(id, content);
}

/** The cache without the given rows; the same map when none were in it. */
export function withoutSavedScripts(
  saved: ReadonlyMap<string, string>,
  ids: Iterable<string>,
): ReadonlyMap<string, string> {
  const gone = [...ids].filter((id) => saved.has(id));
  if (gone.length === 0) return saved;
  const next = new Map(saved);
  for (const id of gone) next.delete(id);
  return next;
}

/** The staged rows whose script the Studio holds no text for — no draft,
 *  nothing it saved or loaded (TTTL-224), nothing fetched before — so the
 *  context has to read them from the server. */
export function unreadScriptRows(
  rowIds: string[],
  drafts: Record<string, string>,
  saved: Record<string, string>,
  fetched: ReadonlyMap<string, string>,
): string[] {
  return rowIds.filter(
    (id) => drafts[id] == null && saved[id] == null && !fetched.has(id),
  );
}
